import React, { useEffect, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet/dist/images/marker-shadow.png";
import { toPoint } from "mgrs";

import explosionIconUrl from "../../assets/false_tgt.png";
import gunIconUrl from "../../assets/gun.png";
import targetIconUrl from "../../assets/target.png";
import unprocessedObservationIconUrl from "../../assets/unprocessed_observation.png";
import processedObservationIconUrl from "../../assets/processed_observation.png";

const normalizeId = (value) =>
  value === null || value === undefined || value === "" ? null : String(value);

const getProcessedObservationId = (obs) =>
  normalizeId(obs?.id ?? obs?.observation_id ?? obs?.processed_observation_id ?? obs?.shot_id);

const MapComponent = ({
  shots = [],
  mgrsZone = "",
  btyCenter = { x: 0, y: 0, z: 0 },
  batteryOptions = [],
  mapCenter,
  processedObservations = [],
  unprocessedObservations = [],
  targetLocation = "",
  lmGeometries = null,
  selectedShotId = null,
  onShotSelect,
  selectedProcessedObservationId = null,
  onProcessedObservationSelect,
  selectedGunId = null,
  onGunSelect,
  height = "100%",
  minHeight = 420,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const offlineLayerRef = useRef(null);
  const layersControlRef = useRef(null);

  const batteryMarkerRef = useRef(null);
  const gunsLayerRef = useRef(null);
  const shotsLayerRef = useRef(null);
  const observationsLayerRef = useRef(null);
  const targetMarkerRef = useRef(null);
  const lmLayerRef = useRef(null);

  const gunMarkersByIdRef = useRef(new Map());
  const shotMarkersByIdRef = useRef(new Map());
  const processedObservationMarkersByIdRef = useRef(new Map());
  const selectedGunHighlightRef = useRef(null);
  const selectedShotHighlightRef = useRef(null);
  const selectedProcessedObservationHighlightRef = useRef(null);

  const tileLayerUrl =
    process.env.REACT_APP_FALLBACK_TILE_LAYER ||
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

  const tileLayerAttribution =
    process.env.REACT_APP_FALLBACK_TILE_LAYER_ATTRIBUTION ||
    "Tiles &copy; Esri";

  const gunIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: gunIconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
    [],
  );

  const selectedGunIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: gunIconUrl,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
      }),
    [],
  );

  const explosionIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: explosionIconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
    [],
  );

  const selectedExplosionIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: explosionIconUrl,
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
      }),
    [],
  );

  const unprocessedObservationIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: unprocessedObservationIconUrl,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
      }),
    [],
  );

  const processedObservationIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: processedObservationIconUrl,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28],
      }),
    [],
  );

  const selectedProcessedObservationIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: processedObservationIconUrl,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      }),
    [],
  );

  const targetIcon = useMemo(
    () =>
      new L.Icon({
        iconUrl: targetIconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      }),
    [],
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [39.39, -8.28],
      zoom: 15,
      maxZoom: 19,
      minZoom: 11,
    });
    mapRef.current = map;

    // Online imagery base (garrison / when connected to the internet).
    const onlineLayer = L.tileLayer(tileLayerUrl, {
      attribution: tileLayerAttribution,
      maxZoom: 19,
    });
    tileLayerRef.current = onlineLayer;
    onlineLayer.addTo(map); // shown immediately; swapped for offline below if present

    gunsLayerRef.current = L.layerGroup().addTo(map);
    shotsLayerRef.current = L.layerGroup().addTo(map);
    observationsLayerRef.current = L.layerGroup().addTo(map);
    lmLayerRef.current = L.layerGroup().addTo(map);

    setTimeout(() => mapRef.current?.invalidateSize(), 0);

    // Discover offline MBTiles tilesets served by the FDC. In the field there is no
    // internet, so if any tileset exists we make it the default base and expose a
    // layer switcher; otherwise we silently stay on the online imagery.
    // Keep rasters crisp: constrain the map's usable zoom range (and extent) to the
    // active base. Below a tileset's minzoom there are no tiles, so Leaflet would
    // scale them up and look blurry — instead we stop zoom-out at the native minzoom.
    const ONLINE_MIN = 3, ONLINE_MAX = 19, OVERZOOM = 2;
    const tsBounds = (ts) =>
      Array.isArray(ts?.bounds) && ts.bounds.length === 4
        ? L.latLngBounds([ts.bounds[1], ts.bounds[0]], [ts.bounds[3], ts.bounds[2]])
        : null;
    const applyZoomRange = (ts) => {
      const m = mapRef.current;
      if (!m) return;
      if (ts && Number.isFinite(ts.minzoom) && Number.isFinite(ts.maxzoom)) {
        const min = ts.minzoom;
        const max = ts.maxzoom + OVERZOOM;
        m.setMinZoom(min);
        m.setMaxZoom(max);
        if (m.getZoom() < min) m.setZoom(min);
        else if (m.getZoom() > max) m.setZoom(max);
        m.setMaxBounds(tsBounds(ts));
      } else {
        m.setMinZoom(ONLINE_MIN);
        m.setMaxZoom(ONLINE_MAX);
        m.setMaxBounds(null);
      }
    };

    let cancelled = false;
    fetch("/api/tiles")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !mapRef.current) return;
        const sets = Array.isArray(data?.tilesets) ? data.tilesets : [];
        const baseLayers = { "Imagery (online)": onlineLayer };
        const tsByLayerId = {};
        let firstOffline = null;
        let firstTs = null;

        sets.forEach((ts) => {
          const layer = L.tileLayer(
            `/api/tiles/${encodeURIComponent(ts.id)}/{z}/{x}/{y}`,
            {
              attribution: ts.attribution || "Offline map",
              // Native zoom span of the tileset — Leaflet serves crisp tiles within
              // it and only scales mildly past it (bounded by OVERZOOM on the map).
              minNativeZoom: Number.isFinite(ts.minzoom) ? ts.minzoom : 0,
              maxNativeZoom: Number.isFinite(ts.maxzoom) ? ts.maxzoom : 19,
              bounds: tsBounds(ts) || undefined,
              maxZoom: 22,
            },
          );
          baseLayers[ts.name || ts.id] = layer;
          tsByLayerId[L.Util.stamp(layer)] = ts;
          if (!firstOffline) {
            firstOffline = layer;
            firstTs = ts;
          }
        });

        if (firstOffline) {
          map.removeLayer(onlineLayer);
          firstOffline.addTo(map);
          offlineLayerRef.current = firstOffline;
          applyZoomRange(firstTs);
          const b = tsBounds(firstTs);
          if (b) map.fitBounds(b);
        }
        if (sets.length) {
          layersControlRef.current = L.control
            .layers(baseLayers, {}, { collapsed: false })
            .addTo(map);
          // Re-tune the zoom range whenever the operator switches base map.
          map.on("baselayerchange", (e) => {
            applyZoomRange(tsByLayerId[L.Util.stamp(e.layer)] || null);
          });
        }
      })
      .catch(() => {
        /* offline tiles unavailable — stay on the online layer */
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      tileLayerRef.current = null;
      offlineLayerRef.current = null;
      layersControlRef.current = null;
      batteryMarkerRef.current = null;
      gunsLayerRef.current = null;
      shotsLayerRef.current = null;
      observationsLayerRef.current = null;
      targetMarkerRef.current = null;
      gunMarkersByIdRef.current = new Map();
      shotMarkersByIdRef.current = new Map();
      processedObservationMarkersByIdRef.current = new Map();
      selectedGunHighlightRef.current = null;
      selectedShotHighlightRef.current = null;
      selectedProcessedObservationHighlightRef.current = null;
    };
  }, [tileLayerUrl, tileLayerAttribution]);

  useEffect(() => {
    if (!mapRef.current) return;
    setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 0);
  });

  useEffect(() => {
    if (!mapRef.current) return;

    if (btyCenter && btyCenter.x && btyCenter.y) {
      try {
        const lat = parseFloat(btyCenter.y);
        const lng = parseFloat(btyCenter.x);

        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          if (batteryMarkerRef.current) {
            mapRef.current.removeLayer(batteryMarkerRef.current);
          }

          batteryMarkerRef.current = L.marker([lat, lng], { icon: gunIcon })
            .addTo(mapRef.current)
            .bindPopup(
              `<b>Battery Center</b><br/>Lat: ${lat}, Lng: ${lng}<br/>Alt: ${btyCenter.z}m`,
            );
        }
      } catch (error) {
        console.error("Error placing battery center marker:", error);
      }
    }
  }, [btyCenter, gunIcon]);

  useEffect(() => {
    if (!gunsLayerRef.current) return;

    gunsLayerRef.current.clearLayers();
    gunMarkersByIdRef.current = new Map();
    if (!Array.isArray(batteryOptions)) return;

    batteryOptions.forEach((gun) => {
      const lat = parseFloat(gun.norths ?? gun.Norths);
      const lng = parseFloat(gun.wests ?? gun.Wests);

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        const normalizedGunId = normalizeId(gun.id);
        const marker = L.marker([lat, lng], { icon: gunIcon }).bindPopup(
          `<b>Gun ${gun.gun_number}</b><br/>MGRS: ${gun.mgrs || "N/A"}<br/>Alt: ${gun.altitude}m`,
        );
        gunsLayerRef.current.addLayer(marker);

        if (normalizedGunId) {
          gunMarkersByIdRef.current.set(normalizedGunId, marker);
          if (typeof onGunSelect === "function") {
            marker.on("click", () => onGunSelect(gun.id));
          }
        }
      }
    });
  }, [batteryOptions, gunIcon, onGunSelect]);

  useEffect(() => {
    if (!shotsLayerRef.current) return;

    shotsLayerRef.current.clearLayers();
    shotMarkersByIdRef.current = new Map();
    if (!Array.isArray(shots)) return;

    shots.forEach((shot) => {
      try {
        const easting = parseFloat(shot.false_tgt_x);
        const northing = parseFloat(shot.false_tgt_y);
        const altitude = shot.false_tgt_z;

        if (!Number.isFinite(easting) || !Number.isFinite(northing) || !mgrsZone) {
          return;
        }

        const eStr = easting.toFixed(0).padStart(5, "0");
        const nStr = northing.toFixed(0).padStart(5, "0");
        const mgrsString = mgrsZone + eStr + nStr;
        const [lng, lat] = toPoint(mgrsString);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const normalizedShotId = normalizeId(shot.shot_id);
        const marker = L.marker([lat, lng], { icon: explosionIcon })
          .addTo(shotsLayerRef.current)
          .bindPopup(
            `<b>Shot ID:</b> ${shot.shot_id}<br/><b>Altitude:</b> ${altitude ?? "N/A"} m<br/><b>Direction:</b> ${shot.direction_deflection || shot.fire_direction_deflection || "N/A"}<br/><b>Range:</b> ${shot.range || "N/A"} m`,
          );

        if (normalizedShotId) {
          shotMarkersByIdRef.current.set(normalizedShotId, marker);
        }

        if (typeof onShotSelect === "function" && normalizedShotId) {
          marker.on("click", () => onShotSelect(shot.shot_id));
        }
      } catch (error) {
        console.error("Error placing shot:", error, shot);
      }
    });
  }, [shots, mgrsZone, explosionIcon, onShotSelect]);

  useEffect(() => {
    if (!observationsLayerRef.current) return;

    observationsLayerRef.current.clearLayers();
    processedObservationMarkersByIdRef.current = new Map();
    if (!Array.isArray(processedObservations) || !Array.isArray(unprocessedObservations)) {
      return;
    }

    const allObs = [...processedObservations, ...unprocessedObservations];

    allObs.forEach((obs) => {
      try {
        const easting = parseFloat(obs.observed_x ?? obs.x);
        const northing = parseFloat(obs.observed_y ?? obs.y);

        if (!Number.isFinite(easting) || !Number.isFinite(northing) || !mgrsZone) {
          return;
        }

        const eStr = easting.toFixed(0).padStart(5, "0");
        const nStr = northing.toFixed(0).padStart(5, "0");
        const mgrsString = mgrsZone + eStr + nStr;
        const [lng, lat] = toPoint(mgrsString);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const isProcessed = Boolean(obs.is_processed);
        const marker = L.marker([lat, lng], {
          icon: isProcessed ? processedObservationIcon : unprocessedObservationIcon,
        })
          .addTo(observationsLayerRef.current)
          .bindPopup(
            `<b>Observation ID:</b> ${obs.id || obs.observation_id || "N/A"}<br/><b>Impact #:</b> ${obs.shot_id || "N/A"}<br/><b>Processed:</b> ${isProcessed ? "Yes" : "No"}<br/>`,
          );

        if (isProcessed) {
          const processedId = getProcessedObservationId(obs);
          if (processedId) {
            processedObservationMarkersByIdRef.current.set(processedId, marker);
            if (typeof onProcessedObservationSelect === "function") {
              marker.on("click", () => onProcessedObservationSelect(processedId));
            }
          }
        }
      } catch (error) {
        console.error("Error placing observation:", error, obs);
      }
    });
  }, [
    processedObservations,
    unprocessedObservations,
    mgrsZone,
    processedObservationIcon,
    unprocessedObservationIcon,
    onProcessedObservationSelect,
  ]);

  useEffect(() => {
    if (!mapRef.current) return;

    const normalizedSelectedGunId = normalizeId(selectedGunId);

    gunMarkersByIdRef.current.forEach((marker, gunId) => {
      const isSelected = normalizedSelectedGunId && gunId === normalizedSelectedGunId;
      marker.setIcon(isSelected ? selectedGunIcon : gunIcon);
      marker.setZIndexOffset(isSelected ? 1200 : 0);
    });

    if (selectedGunHighlightRef.current) {
      mapRef.current.removeLayer(selectedGunHighlightRef.current);
      selectedGunHighlightRef.current = null;
    }

    if (!normalizedSelectedGunId) return;

    const selectedMarker = gunMarkersByIdRef.current.get(normalizedSelectedGunId);
    if (!selectedMarker) return;

    const latlng = selectedMarker.getLatLng();
    selectedGunHighlightRef.current = L.circleMarker(latlng, {
      radius: 17,
      color: "#fde68a",
      weight: 2,
      fillColor: "#f59e0b",
      fillOpacity: 0.12,
      opacity: 0.95,
    }).addTo(mapRef.current);

    mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 16), {
      duration: 0.45,
    });
    selectedMarker.openPopup();
  }, [selectedGunId, batteryOptions, gunIcon, selectedGunIcon]);

  useEffect(() => {
    if (!mapRef.current) return;

    const normalizedSelectedProcessedObservationId = normalizeId(selectedProcessedObservationId);

    processedObservationMarkersByIdRef.current.forEach((marker, obsId) => {
      const isSelected = normalizedSelectedProcessedObservationId && obsId === normalizedSelectedProcessedObservationId;
      marker.setIcon(isSelected ? selectedProcessedObservationIcon : processedObservationIcon);
      marker.setZIndexOffset(isSelected ? 1100 : 0);
    });

    if (selectedProcessedObservationHighlightRef.current) {
      mapRef.current.removeLayer(selectedProcessedObservationHighlightRef.current);
      selectedProcessedObservationHighlightRef.current = null;
    }

    if (!normalizedSelectedProcessedObservationId) return;

    const selectedMarker = processedObservationMarkersByIdRef.current.get(
      normalizedSelectedProcessedObservationId,
    );
    if (!selectedMarker) return;

    const latlng = selectedMarker.getLatLng();
    selectedProcessedObservationHighlightRef.current = L.circleMarker(latlng, {
      radius: 16,
      color: "#fde68a",
      weight: 2,
      fillColor: "#fbbf24",
      fillOpacity: 0.16,
      opacity: 0.95,
    }).addTo(mapRef.current);

    mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 16), {
      duration: 0.45,
    });
    selectedMarker.openPopup();
  }, [
    selectedProcessedObservationId,
    processedObservations,
    processedObservationIcon,
    selectedProcessedObservationIcon,
  ]);

  useEffect(() => {
    if (!mapRef.current) return;

    const normalizedSelectedShotId = normalizeId(selectedShotId);

    shotMarkersByIdRef.current.forEach((marker, shotId) => {
      const isSelected = normalizedSelectedShotId && shotId === normalizedSelectedShotId;
      marker.setIcon(isSelected ? selectedExplosionIcon : explosionIcon);
      marker.setZIndexOffset(isSelected ? 1000 : 0);
    });

    if (selectedShotHighlightRef.current) {
      mapRef.current.removeLayer(selectedShotHighlightRef.current);
      selectedShotHighlightRef.current = null;
    }

    if (!normalizedSelectedShotId) return;

    const selectedMarker = shotMarkersByIdRef.current.get(normalizedSelectedShotId);
    if (!selectedMarker) return;

    const latlng = selectedMarker.getLatLng();
    selectedShotHighlightRef.current = L.circleMarker(latlng, {
      radius: 18,
      color: "#fbbf24",
      weight: 2,
      fillColor: "#f59e0b",
      fillOpacity: 0.14,
      opacity: 0.95,
    }).addTo(mapRef.current);

    mapRef.current.flyTo(latlng, Math.max(mapRef.current.getZoom(), 16), {
      duration: 0.45,
    });
    selectedMarker.openPopup();
  }, [selectedShotId, shots, explosionIcon, selectedExplosionIcon]);

  useEffect(() => {
    if (!mapRef.current || !targetLocation) return;

    try {
      const [lng, lat] = toPoint(targetLocation);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        if (targetMarkerRef.current) {
          mapRef.current.removeLayer(targetMarkerRef.current);
        }

        targetMarkerRef.current = L.marker([lat, lng], { icon: targetIcon })
          .addTo(mapRef.current)
          .bindPopup(`<b>Target Location</b><br/>${targetLocation}`);

        mapRef.current.setView([lat, lng], 15);
      }
    } catch (error) {
      console.error("Error converting target MGRS:", error);
    }
  }, [targetLocation, targetIcon]);

  useEffect(() => {
    if (mapCenter && mapRef.current) {
      const { lat, lng } = mapCenter;
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        mapRef.current.setView([lat, lng], 15);
      }
    }
  }, [mapCenter]);

  // ---- Loitering-Munition mission geometry overlay ------------------------
  // Draws the LM call-for-fire geometries (target, limits, ditch, mandatory
  // route, physical obstacles) as Leaflet layers and fits the view to them.
  // Geometry coords are [lat, lon, alt]; RF obstacles carry no coordinates so
  // they are shown in the side panel only.
  useEffect(() => {
    const map = mapRef.current;
    const layer = lmLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!lmGeometries) return;

    const toLatLngs = (coords) => (coords || []).map((c) => [c[0], c[1]]);
    const drawn = [];
    const addPoly = (geom, style, label) => {
      if (!geom || !geom.coords || geom.coords.length < 2) return;
      const closed = geom.geometryType !== "ROUTE";
      const pts = toLatLngs(geom.coords);
      const shape = closed
        ? L.polygon(pts, style)
        : L.polyline(pts, style);
      if (label) shape.bindPopup(label);
      shape.addTo(layer);
      drawn.push(shape);
    };

    const g = lmGeometries;
    // Popup title prefers the object's own name/callsign; falls back to the field.
    const nm = (geom, fallback) => (geom && geom.name ? geom.name : fallback);
    // Limits — the inclusion boundary the LM must stay within.
    addPoly(g.limits, { color: "#ffd54f", weight: 2, fillOpacity: 0.05 },
      `<b>${nm(g.limits, "Limits")}</b>${g.limits?.altitudeAMSL != null ? `<br/>max ${g.limits.altitudeAMSL} m AMSL` : ""}`);
    // Ditch area — keep-out.
    addPoly(g.ditch, { color: "#ff9800", weight: 2, dashArray: "6 4", fillOpacity: 0.12 },
      `<b>${nm(g.ditch, "Ditch area")}</b>`);
    // Physical obstacles — keep-out, each labelled with its name/type.
    (g.obstacles || []).forEach((o, i) =>
      addPoly(o, { color: "#e040fb", weight: 2, dashArray: "6 4", fillOpacity: 0.12 },
        `<b>${nm(o, `Obstacle ${i + 1}`)}</b>${o.type ? `<br/>${o.type}` : ""}${o.altitudeAMSL != null ? `<br/>${o.altitudeAMSL} m AMSL` : ""}`));
    // Mandatory route — waypoints polyline.
    addPoly(g.route, { color: "#40c4ff", weight: 3 },
      `<b>${nm(g.route, "Mandatory route")}</b>${g.route?.altitudeAMSL != null ? `<br/>${g.route.altitudeAMSL} m AMSL` : ""}`);
    if (g.route && g.route.coords) {
      toLatLngs(g.route.coords).forEach((p) => {
        const wp = L.circleMarker(p, { radius: 4, color: "#40c4ff", fillColor: "#40c4ff", fillOpacity: 1 }).addTo(layer);
        drawn.push(wp);
      });
    }
    // Target — polygon (area) or point, plus a target marker at the centroid.
    if (g.target && g.target.coords && g.target.coords.length) {
      if (g.target.geometryType === "POINT") {
        const [lat, lng] = toLatLngs(g.target.coords)[0];
        const m = L.marker([lat, lng], { icon: targetIcon }).bindPopup(
          `<b>${nm(g.target, "Target")}</b>${g.target.altitudeAMSL != null ? `<br/>${g.target.altitudeAMSL} m AMSL` : ""}`);
        m.addTo(layer);
        drawn.push(m);
      } else {
        addPoly(g.target, { color: "#ff5252", weight: 2, fillOpacity: 0.1 },
          `<b>${nm(g.target, "Target area")}</b>${g.target.altitudeAMSL != null ? `<br/>${g.target.altitudeAMSL} m AMSL` : ""}`);
        const pts = toLatLngs(g.target.coords);
        const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
        const lng = pts.reduce((s, p) => s + p[1], 0) / pts.length;
        const m = L.marker([lat, lng], { icon: targetIcon }).bindPopup(`<b>${nm(g.target, "Target")}</b>`);
        m.addTo(layer);
        drawn.push(m);
      }
    }

    // Fit the view to the mission, anchored on the target so a single stray or
    // mis-captured geometry (e.g. a route accidentally drawn far away) can't blow
    // the view up to span half a continent and shrink every real shape to a dot.
    // Everything is still drawn above; we just exclude far outliers from the FIT.
    if (drawn.length) {
      const haversineKm = (a, b) => {
        const R = 6371;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(b[0] - a[0]);
        const dLon = toRad(b[1] - a[1]);
        const s =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(s));
      };
      // Anchor = target centroid if present, else centroid of all vertices.
      const allPts = [];
      Object.keys(g).forEach((k) => {
        const items = k === "obstacles" ? g[k] || [] : [g[k]];
        (items || []).forEach((geom) => {
          if (geom && geom.coords) toLatLngs(geom.coords).forEach((p) => allPts.push(p));
        });
      });
      const anchorSrc =
        g.target && g.target.coords && g.target.coords.length
          ? toLatLngs(g.target.coords)
          : allPts;
      const anchor = anchorSrc.length
        ? [
            anchorSrc.reduce((s, p) => s + p[0], 0) / anchorSrc.length,
            anchorSrc.reduce((s, p) => s + p[1], 0) / anchorSrc.length,
          ]
        : null;
      // Keep only points within a sane operational radius of the anchor (LM
      // missions are local); if that leaves too little, fall back to everything.
      const OUTLIER_KM = 50;
      let fitPts = anchor
        ? allPts.filter((p) => haversineKm(anchor, p) <= OUTLIER_KM)
        : allPts;
      if (fitPts.length < 2) fitPts = allPts;
      try {
        map.fitBounds(L.latLngBounds(fitPts), { padding: [40, 40], maxZoom: 16 });
      } catch (e) {
        /* ignore degenerate bounds */
      }
    }
  }, [lmGeometries, targetIcon]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height,
        minHeight,
      }}
    />
  );
};

export default MapComponent;
